import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProgression } from '../../auth/ProgressionProvider';
import { useSeason } from '../../auth/SeasonProvider';
import { listBuiltinCirclesTracks } from '@shared/circlesBuiltinMaps';
import { chartDisplayBpm, chartDurationSeconds, formatDuration, stripVariantSuffix } from '@shared/chart';
import { formatMapperCredit } from '@shared/circlesCredit';
import { CIRCLES_VARIANTS, type CirclesVariant, variantLabel } from '@shared/gameTypes';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { isTutorialUnseen } from '../../game/tutorialFlag';
import { TrophyStrip } from '../../components/TrophyStrip';
import { ModeSeoSection } from '../ModeSeoSection';
import '../../components/trophyStrip.css';
import '../home.css';
import './circlesHome.css';

const VARIANT_STORAGE_KEY = 'gravitydash:circles-home-variant:v1';

function loadStoredVariant(): CirclesVariant {
  try {
    const raw = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (raw && (CIRCLES_VARIANTS as ReadonlyArray<string>).includes(raw)) {
      return raw as CirclesVariant;
    }
  } catch (err) {
    console.warn('Failed to read variant from localStorage:', err);
  }
  return 'normal';
}

function persistVariant(variant: CirclesVariant): void {
  try {
    localStorage.setItem(VARIANT_STORAGE_KEY, variant);
  } catch (err) {
    console.warn('Failed to persist variant to localStorage:', err);
  }
}

const VARIANT_DESCRIPTIONS: Record<CirclesVariant, string> = {
  normal: 'Click-only on-ramp. The full track at a friendly pace — every slider is a single tap.',
  hard: 'Same calm pace, now with sliders. Click the head and trace the path to the end.',
  extreme: 'More circles per bar, tighter clusters, smaller approach windows.',
  insane: 'Maximum density. Stream patterns and quick aim shifts throughout.',
};

/**
 * Circles-mode home page. Mirrors the Pulse home but reads from the
 * Circles builtin catalogue and points every link into the
 * `/circles/*` namespace. The variant tabs use a separate storage key
 * (`circles-home-variant`) so a player who plays Pulse on Hard can stay
 * on Circles Normal without the two pages clobbering each other.
 */
export function CirclesHomePage() {
  const { forMap } = useProgression();
  const { crownsForMap } = useSeason();
  const tracks = listBuiltinCirclesTracks();
  const navigate = useNavigate();
  const [variant, setVariant] = useState<CirclesVariant>(() => loadStoredVariant());

  useEffect(() => {
    saveModePref('circles');
  }, []);

  useEffect(() => {
    // Per-mode tutorial gate: a brand-new Circles player gets diverted
    // to /circles/tutorial on first arrival, the same way Pulse's home
    // diverts new Pulse players to /pulse/tutorial. The route-level
    // AuthGate guarantees auth is ready before we mount, so the
    // tutorial flag check is safe to run unconditionally.
    if (isTutorialUnseen('circles')) {
      navigate(modePath('circles', 'tutorial'), { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== VARIANT_STORAGE_KEY && event.key !== null) {
        return;
      }
      setVariant(loadStoredVariant());
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleVariantChange = (next: CirclesVariant) => {
    setVariant(next);
    persistVariant(next);
  };

  // Surface the user's last-played Circles track first when present.
  // Falls back to On & On (the easiest tier-1 song) for first-time visits.
  const featuredTrack = tracks[0];
  const featuredChart = featuredTrack.variants[variant].chart;

  return (
    <div className="home circles-home">
      <header className="home-hero">
        <h1 className="home-hero-kicker">
          BEAT<span style={{ color: 'var(--accent-magenta)' }}>CIRCLES</span>
        </h1>
        <div className="home-cta">
          <Link
            to={modePath('circles', `play/${featuredChart.slug}`)}
            className="btn btn-primary btn-large"
            data-testid="circles-play-cta"
          >
            Play {stripVariantSuffix(featuredChart)}
          </Link>
          <Link to={modePath('circles', 'tutorial')} className="btn">
            How to play
          </Link>
          <Link to="/calibrate" className="btn btn-ghost">
            Calibrate audio
          </Link>
        </div>
      </header>

      <section className="home-maps">
        <div className="home-maps-header">
          <h2>Tracks</h2>
          <p className="muted faint">
            All 12 songs, four difficulty variants each.
          </p>
        </div>

        <div
          className="variant-selector"
          role="group"
          aria-label="Circle density"
          data-testid="circles-variant-selector"
        >
          {CIRCLES_VARIANTS.map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={variant === v}
              className={`variant-tab variant-tab-${v} ${variant === v ? 'is-active' : ''}`}
              onClick={() => handleVariantChange(v)}
              data-testid={`circles-variant-tab-${v}`}
            >
              {variantLabel(v)}
            </button>
          ))}
          <p className="variant-hint muted" aria-live="polite">
            {VARIANT_DESCRIPTIONS[variant]}
          </p>
        </div>

        <ol className="map-list">
          {tracks.map((track, idx) => {
            const chart = track.variants[variant].chart;
            const circleCount = chart.hitObjects.filter((h) => h.kind !== 'spinner').length;
            const trophies = forMap(chart.slug, 'circles');
            const mapperCredit = formatMapperCredit(chart.mapper);
            return (
              <li key={track.baseSlug} className="map-list-item">
                <Link
                  to={modePath('circles', `play/${chart.slug}`)}
                  className="map-row circles-map-row"
                  data-testid={`circles-map-card-${track.baseSlug}`}
                  style={
                    {
                      '--map-bg': chart.palette.bg,
                      '--map-bg-accent': chart.palette.bgAccent,
                      '--map-pad': chart.palette.pad,
                      '--map-player': chart.palette.player,
                    } as React.CSSProperties
                  }
                >
                  <div className="map-row-index">{String(idx + 1).padStart(2, '0')}</div>
                  <div className="map-row-preview circles-map-row-preview">
                    <div className="circles-preview-ring circles-preview-ring-a" />
                    <div className="circles-preview-ring circles-preview-ring-b" />
                    <div className="circles-preview-ring circles-preview-ring-c" />
                  </div>
                  <div className="map-row-body">
                    <div className="map-row-title">{stripVariantSuffix(chart)}</div>
                    <div className="map-row-desc">{chart.description}</div>
                    {mapperCredit && (
                      <div
                        className="map-row-mapper muted faint"
                        data-testid={`circles-map-mapper-${track.baseSlug}`}
                      >
                        {mapperCredit}
                      </div>
                    )}
                  </div>
                  <div className="map-row-meta">
                    <div className="map-row-bpm">{chartDisplayBpm(chart.bpm).toFixed(0)} BPM</div>
                    <div className="map-row-duration" title="Duration">
                      {formatDuration(chartDurationSeconds(chart.bpm, chart.totalBeats))}
                    </div>
                    <div
                      className="map-row-pad-count"
                      title={`${circleCount} circles on ${variantLabel(variant)}`}
                    >
                      {circleCount} circles
                    </div>
                    <div
                      className="map-row-difficulty"
                      aria-label={`Difficulty ${chart.difficulty}`}
                    >
                      {Array.from({ length: 5 }, (_, i) => (
                        <span
                          key={i}
                          className={i < chart.difficulty ? 'pip pip-on' : 'pip'}
                        />
                      ))}
                    </div>
                    <TrophyStrip
                      stars={trophies.stars}
                      cups={trophies.cups}
                      crowns={crownsForMap(chart.slug, 'circles')}
                      size="sm"
                    />
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      <ModeSeoSection
        mode="circles"
        playHref={modePath('circles', `play/${featuredChart.slug}`)}
        playLabel={`Play ${stripVariantSuffix(featuredChart)}`}
      />
    </div>
  );
}
